import { Link } from 'react-router-dom';
import SEOHead from '../../components/SEOHead';
import { Button } from '../../components/ui/button';
import { ArrowRight, Calculator, FileText, BarChart3, Car, ShieldCheck, ScanLine, Layers, ChevronRight, Receipt, CheckCircle2 } from 'lucide-react';

const FAQ_DATA = [
    { q: "Quel logiciel utiliser pour suivre les marges par véhicule ?", a: "Marge Auto est un logiciel en ligne conçu pour les professionnels de l'automobile. Il permet de suivre la marge réelle de chaque véhicule en centralisant l'achat, la vente, les frais, la TVA, le SAV et les commissions commerciales sur un seul dossier." },
    { q: "Comment calculer la marge d'un véhicule d'occasion ?", a: "La marge réelle d'un véhicule d'occasion ne se résume pas à la différence entre le prix de vente et le prix d'achat. Il faut déduire les frais de préparation, d'entretien, de transport, de garantie, ainsi que la TVA applicable. Marge Auto automatise ce calcul pour chaque véhicule." },
    { q: "Quels frais faut-il prendre en compte dans la rentabilité d'un véhicule ?", a: "Les frais courants comprennent : contrôle technique, révision, mécanique, pneumatiques, carrosserie, préparation esthétique, transport, garantie et tout autre frais spécifique au véhicule. Marge Auto permet de les rattacher par immatriculation." },
    { q: "Peut-on suivre la marge de chaque commercial ?", a: "Oui. Marge Auto calcule les commissions commerciales par commercial et par mois, en se basant sur la marge nette des véhicules vendus, les retenues SAV et les frais applicables." },
    { q: "Peut-on rattacher les factures aux véhicules ?", a: "Oui. Marge Auto propose un rattachement par immatriculation, une saisie en masse des frais, et un scanner intelligent de factures qui extrait automatiquement les informations et propose le rattachement au bon véhicule." },
];

const SCHEMA = {
    "@context": "https://schema.org",
    "@graph": [
        {
            "@type": "SoftwareApplication",
            "name": "Marge Auto",
            "applicationCategory": "BusinessApplication",
            "operatingSystem": "Web",
            "description": "Logiciel de suivi de marge par véhicule pour professionnels de l'automobile.",
            "url": "https://marge-auto.com/suivi-marge-par-vehicule",
            "offers": { "@type": "Offer", "price": "0", "priceCurrency": "EUR", "description": "Essai gratuit 30 jours" }
        },
        {
            "@type": "FAQPage",
            "mainEntity": FAQ_DATA.map(f => ({
                "@type": "Question",
                "name": f.q,
                "acceptedAnswer": { "@type": "Answer", "text": f.a }
            }))
        },
        {
            "@type": "BreadcrumbList",
            "itemListElement": [
                { "@type": "ListItem", "position": 1, "name": "Accueil", "item": "https://marge-auto.com/" },
                { "@type": "ListItem", "position": 2, "name": "Suivi marge par véhicule", "item": "https://marge-auto.com/suivi-marge-par-vehicule" }
            ]
        }
    ]
};

export default function PageSuiviMarge() {
    return (
        <div className="min-h-screen bg-white text-gray-900">
            <SEOHead
                title="Logiciel pour suivre les marges par véhicule | Marge Auto"
                description="Suivez la marge réelle de chaque véhicule : achat, frais, TVA, SAV, remises et commissions. Marge Auto centralise la rentabilité de vos VO."
                path="/suivi-marge-par-vehicule"
                schema={SCHEMA}
            />

            {/* Nav */}
            <nav className="border-b border-gray-100 bg-white/80 backdrop-blur-sm sticky top-0 z-50">
                <div className="max-w-6xl mx-auto px-6 py-4 flex items-center justify-between">
                    <Link to="/landing" className="text-xl font-bold tracking-tight text-[#6366F1]">Marge Auto</Link>
                    <div className="flex items-center gap-4">
                        <Link to="/auth" className="text-sm text-gray-500 hover:text-gray-900 hidden sm:inline">Se connecter</Link>
                        <Link to="/landing"><Button className="bg-[#6366F1] hover:bg-[#4F46E5] text-white text-sm h-9 px-4 rounded-lg">Demander un accès</Button></Link>
                    </div>
                </div>
            </nav>

            {/* Breadcrumb */}
            <div className="max-w-6xl mx-auto px-6 py-3">
                <nav className="flex items-center gap-1.5 text-sm text-gray-400" aria-label="Fil d'Ariane">
                    <Link to="/landing" className="hover:text-gray-600">Accueil</Link>
                    <ChevronRight className="w-3.5 h-3.5" />
                    <span className="text-gray-700 font-medium">Suivi marge par véhicule</span>
                </nav>
            </div>

            {/* Hero */}
            <header className="bg-gradient-to-b from-gray-950 to-gray-900 text-white py-20 sm:py-28">
                <div className="max-w-4xl mx-auto px-6 text-center">
                    <p className="text-sm font-medium text-indigo-400 uppercase tracking-wider mb-4">Suivi de rentabilité automobile</p>
                    <h1 className="text-4xl sm:text-5xl lg:text-6xl font-bold tracking-tight leading-tight mb-6">Le logiciel pour suivre vos marges par véhicule</h1>
                    <p className="text-lg text-gray-400 max-w-2xl mx-auto mb-8">Connaissez la marge réelle de chaque véhicule en centralisant le prix d'achat, le prix de vente et l'ensemble des frais associés. Marge Auto automatise le suivi de rentabilité pour les professionnels de l'automobile.</p>
                    <div className="flex flex-col sm:flex-row items-center justify-center gap-3">
                        <Link to="/landing"><Button className="bg-[#6366F1] hover:bg-[#4F46E5] text-white h-12 px-8 text-base rounded-xl w-full sm:w-auto">Découvrir Marge Auto<ArrowRight className="ml-2 w-4 h-4" /></Button></Link>
                        <Link to="/landing" className="text-sm text-gray-400 hover:text-white transition-colors underline underline-offset-4">Demander un accès</Link>
                    </div>
                </div>
            </header>

            {/* Section: Pourquoi suivre la marge par véhicule */}
            <section className="py-16 sm:py-20 bg-white">
                <div className="max-w-4xl mx-auto px-6">
                    <h2 className="text-2xl sm:text-3xl font-bold mb-6">Pourquoi suivre la marge de chaque véhicule ?</h2>
                    <p className="text-gray-600 leading-relaxed mb-4">Un véhicule acheté 15 000 € et revendu 19 000 € ne génère pas nécessairement 4 000 € de marge. Entre l'achat et la vente, de nombreuses dépenses viennent réduire la rentabilité réelle du dossier.</p>
                    <p className="text-gray-600 leading-relaxed mb-8">Sans un suivi précis, véhicule par véhicule, il est difficile d'identifier les opérations rentables, les postes de dépenses excessifs, ou les véhicules qui consomment la marge de l'agence.</p>
                    <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-3">
                        {['Contrôle technique', 'Révision / entretien', 'Pneumatiques', 'Freinage', 'Préparation esthétique', 'Carburant / convoyage', 'Garantie', 'Réparations mécaniques', 'SAV', 'Remise commerciale', 'Carrosserie / peinture', 'Transport'].map(f => (
                            <div key={f} className="flex items-center gap-2 px-3 py-2.5 rounded-lg bg-gray-50 border border-gray-100 text-sm text-gray-700">
                                <span className="w-1.5 h-1.5 rounded-full bg-[#6366F1] shrink-0" />{f}
                            </div>
                        ))}
                    </div>
                    <p className="text-sm text-gray-500 mt-6">Marge Auto centralise ces éléments sur chaque dossier véhicule afin de suivre la rentabilité réelle de chaque opération.</p>
                </div>
            </section>

            {/* Section: Exemple concret */}
            <section className="py-16 sm:py-20 bg-gray-50">
                <div className="max-w-3xl mx-auto px-6">
                    <h2 className="text-2xl sm:text-3xl font-bold mb-8 text-center">Exemple de calcul de marge d'un véhicule</h2>
                    <div className="bg-gray-950 text-white rounded-2xl p-6 sm:p-8 font-mono text-sm leading-loose shadow-xl">
                        <div className="flex justify-between"><span className="text-gray-400">Achat véhicule</span><span>15 000,00 €</span></div>
                        <div className="flex justify-between"><span className="text-gray-400">Vente véhicule</span><span>19 000,00 €</span></div>
                        <div className="border-t border-gray-700 my-3" />
                        <div className="flex justify-between"><span className="text-gray-400">Différence brute</span><span>4 000,00 €</span></div>
                        <div className="border-t border-gray-800 my-2" />
                        <div className="flex justify-between text-gray-400"><span>Révision + CT</span><span>– 450,00 €</span></div>
                        <div className="flex justify-between text-gray-400"><span>Carrosserie</span><span>– 380,00 €</span></div>
                        <div className="flex justify-between text-gray-400"><span>Nettoyage / detailing</span><span>– 120,00 €</span></div>
                        <div className="flex justify-between text-gray-400"><span>Transport</span><span>– 250,00 €</span></div>
                        <div className="border-t border-gray-700 my-3" />
                        <div className="flex justify-between"><span className="text-gray-400">Total frais véhicule</span><span>– 1 200,00 €</span></div>
                        <div className="border-t border-gray-700 my-3" />
                        <div className="flex justify-between text-indigo-400 font-bold text-base"><span>Marge après frais</span><span>2 800,00 €</span></div>
                    </div>
                    <p className="text-sm text-gray-400 text-center mt-4">Cet exemple est simplifié à des fins pédagogiques. Le traitement fiscal (TVA sur marge, TVA récupérable) dépend du régime applicable à chaque opération.</p>
                </div>
            </section>

            {/* Section: Ce que Marge Auto prend en compte */}
            <section className="py-16 sm:py-20 bg-white">
                <div className="max-w-5xl mx-auto px-6">
                    <h2 className="text-2xl sm:text-3xl font-bold text-center mb-12">Une vision complète de la rentabilité par véhicule</h2>
                    <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6">
                        {[
                            { icon: Car, title: 'Achat et vente', text: "Suivi du prix d'achat, du prix de vente et des remises client pour chaque véhicule." },
                            { icon: FileText, title: 'Frais du véhicule', text: "Centralisation des dépenses rattachées au dossier par immatriculation, avec anti-doublon et scanner de factures." },
                            { icon: Calculator, title: 'TVA', text: "Prise en compte des mécanismes de TVA gérés par Marge Auto, adaptés au régime applicable." },
                            { icon: ShieldCheck, title: 'SAV', text: "Visualisation de l'impact des coûts après-vente et des retenues sur les commissions commerciales." },
                            { icon: Receipt, title: 'Commissions', text: "Calcul automatique des commissions par commercial selon les règles configurées par l'agence." },
                            { icon: Layers, title: 'Frais mutualisés', text: "Répartition des dépenses partagées (nettoyage, consommables) sur les véhicules vendus de la période." },
                            { icon: ScanLine, title: 'Scanner de factures', text: "Extraction des informations, détection de l'immatriculation et proposition de rattachement au véhicule." },
                            { icon: BarChart3, title: "Tableau de bord", text: "Vue d'ensemble de la rentabilité de l'agence : marges, CA, commissions, objectifs." },
                            { icon: FileText, title: 'Exports', text: "Export Excel et PDF des commissions et du compte d'exploitation pour votre comptabilité." },
                        ].map(f => (
                            <div key={f.title} className="bg-gray-50 rounded-xl border border-gray-100 p-5 hover:border-[#6366F1]/30 transition-colors">
                                <f.icon className="w-6 h-6 text-[#6366F1] mb-3" />
                                <h3 className="font-semibold mb-1">{f.title}</h3>
                                <p className="text-sm text-gray-500">{f.text}</p>
                            </div>
                        ))}
                    </div>
                </div>
            </section>

            {/* Section: Du frais à la marge — visual pipeline */}
            <section className="py-16 sm:py-20 bg-gray-50">
                <div className="max-w-2xl mx-auto px-6 text-center">
                    <h2 className="text-2xl sm:text-3xl font-bold mb-10">Du frais à la marge</h2>
                    <div className="space-y-0">
                        {[
                            { label: 'Achat du véhicule', sub: "Prix d'achat enregistré" },
                            { label: 'Vente du véhicule', sub: 'Prix de vente et remise éventuelle' },
                            { label: 'Frais véhicule', sub: 'CT, entretien, pneus, carrosserie, transport...' },
                            { label: 'TVA applicable', sub: 'Selon le régime en vigueur' },
                            { label: 'SAV / autres coûts', sub: 'Retenues et dépenses après-vente' },
                            { label: 'Marge réelle du dossier', sub: 'Rentabilité nette de chaque véhicule', highlight: true },
                        ].map((s, i) => (
                            <div key={i}>
                                <div className={`rounded-xl px-6 py-4 mx-auto max-w-md ${s.highlight ? 'bg-[#6366F1] text-white shadow-lg' : 'bg-white border border-gray-200'}`}>
                                    <p className={`font-semibold ${s.highlight ? 'text-white' : 'text-gray-900'}`}>{s.label}</p>
                                    <p className={`text-sm ${s.highlight ? 'text-indigo-200' : 'text-gray-400'}`}>{s.sub}</p>
                                </div>
                                {i < 5 && <div className="flex justify-center py-1"><div className="w-0.5 h-6 bg-gray-300" /></div>}
                            </div>
                        ))}
                    </div>
                </div>
            </section>

            {/* Section: Pour les professionnels */}
            <section className="py-16 sm:py-20 bg-white">
                <div className="max-w-4xl mx-auto px-6">
                    <h2 className="text-2xl sm:text-3xl font-bold mb-8">Un suivi de marge conçu pour les professionnels de l'automobile</h2>
                    <div className="grid sm:grid-cols-2 gap-6">
                        {[
                            { title: 'Marchands automobiles', text: "Suivez la marge de chaque véhicule acheté et revendu, du premier frais à la vente finale." },
                            { title: 'Négociants automobiles', text: "Identifiez rapidement les opérations rentables et celles qui nécessitent une attention particulière." },
                            { title: 'Agences avec équipe commerciale', text: "Suivez les performances par commercial avec des commissions calculées sur la marge réelle." },
                            { title: 'Professionnels VO indépendants', text: "Même avec quelques véhicules par mois, connaître sa marge réelle permet de meilleures décisions." },
                        ].map(p => (
                            <div key={p.title} className="border-l-4 border-[#6366F1] pl-5 py-2">
                                <h3 className="font-semibold mb-1">{p.title}</h3>
                                <p className="text-sm text-gray-500">{p.text}</p>
                            </div>
                        ))}
                    </div>
                </div>
            </section>

            {/* Section: Tableur vs Marge Auto */}
            <section className="py-16 sm:py-20 bg-gray-50">
                <div className="max-w-4xl mx-auto px-6">
                    <h2 className="text-2xl sm:text-3xl font-bold text-center mb-10">Pourquoi remplacer le suivi des marges sur Excel ?</h2>
                    <div className="grid md:grid-cols-2 gap-8">
                        <div className="bg-white rounded-xl border border-gray-200 p-6">
                            <p className="text-sm font-semibold text-gray-400 uppercase tracking-wider mb-4">Tableur classique</p>
                            <ul className="space-y-2.5 text-sm text-gray-500">
                                {['Saisies dispersées dans plusieurs fichiers', 'Factures stockées séparément', 'Mises à jour manuelles à chaque frais', "Pas de vision consolidée par commercial", "Risque d'erreur ou d'oubli"].map(t => (
                                    <li key={t} className="flex items-start gap-2"><span className="w-1.5 h-1.5 mt-1.5 rounded-full bg-gray-300 shrink-0" />{t}</li>
                                ))}
                            </ul>
                        </div>
                        <div className="bg-white rounded-xl border-2 border-[#6366F1]/30 p-6">
                            <p className="text-sm font-semibold text-[#6366F1] uppercase tracking-wider mb-4">Marge Auto</p>
                            <ul className="space-y-2.5 text-sm text-gray-700">
                                {['Dossier véhicule centralisé', 'Frais rattachés par immatriculation', "Marge actualisée à chaque ajout", 'Commissions, SAV et tableaux de bord', 'Factures scannées et liées au dossier'].map(t => (
                                    <li key={t} className="flex items-start gap-2"><CheckCircle2 className="w-4 h-4 mt-0.5 text-[#6366F1] shrink-0" />{t}</li>
                                ))}
                            </ul>
                        </div>
                    </div>
                </div>
            </section>

            {/* FAQ */}
            <section className="py-16 sm:py-20 bg-white">
                <div className="max-w-3xl mx-auto px-6">
                    <h2 className="text-2xl sm:text-3xl font-bold text-center mb-10">Questions fréquentes</h2>
                    {FAQ_DATA.map((faq, i) => (
                        <details key={i} className="mb-3 bg-gray-50 rounded-xl border border-gray-200 overflow-hidden">
                            <summary className="px-5 py-4 cursor-pointer font-medium text-gray-900 hover:bg-gray-100/50">{faq.q}</summary>
                            <p className="px-5 pb-4 text-sm text-gray-500 leading-relaxed">{faq.a}</p>
                        </details>
                    ))}
                </div>
            </section>

            {/* Internal links */}
            <section className="py-12 bg-gray-50 border-t border-gray-100">
                <div className="max-w-4xl mx-auto px-6">
                    <p className="text-sm text-gray-400 mb-4">En savoir plus sur Marge Auto :</p>
                    <div className="flex flex-wrap gap-3">
                        <Link to="/logiciel-marchand-automobile" className="text-sm text-[#6366F1] hover:underline">Logiciel pour marchand automobile</Link>
                        <span className="text-gray-300">·</span>
                        <Link to="/calcul-marge-vehicule-occasion" className="text-sm text-[#6366F1] hover:underline">Calcul de marge d'un véhicule d'occasion</Link>
                        <span className="text-gray-300">·</span>
                        <Link to="/gestion-frais-vehicules" className="text-sm text-[#6366F1] hover:underline">Gestion des frais véhicules</Link>
                    </div>
                </div>
            </section>

            {/* CTA */}
            <section className="py-16 bg-gray-950 text-white text-center">
                <div className="max-w-2xl mx-auto px-6">
                    <h2 className="text-2xl sm:text-3xl font-bold mb-4">Suivez la marge de chaque véhicule</h2>
                    <p className="text-gray-400 mb-8">Essayez Marge Auto gratuitement pendant 30 jours.</p>
                    <Link to="/landing"><Button className="bg-[#6366F1] hover:bg-[#4F46E5] text-white h-12 px-8 text-base rounded-xl">Découvrir Marge Auto<ArrowRight className="ml-2 w-4 h-4" /></Button></Link>
                </div>
            </section>

            {/* Footer */}
            <footer className="py-10 bg-white border-t border-gray-100">
                <div className="max-w-5xl mx-auto px-6 flex flex-col sm:flex-row items-center justify-between gap-4 text-sm text-gray-400">
                    <span>&copy; {new Date().getFullYear()} Marge Auto</span>
                    <div className="flex flex-wrap items-center gap-4">
                        <Link to="/logiciel-marchand-automobile" className="hover:text-gray-700">Logiciel marchand auto</Link>
                        <Link to="/calcul-marge-vehicule-occasion" className="hover:text-gray-700">Calcul de marge VO</Link>
                        <Link to="/gestion-frais-vehicules" className="hover:text-gray-700">Gestion des frais</Link>
                        <Link to="/suivi-marge-par-vehicule" className="hover:text-gray-700">Suivi marge véhicule</Link>
                    </div>
                </div>
            </footer>
        </div>
    );
}
